import { ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { trackCta, type CtaId, type TrackOptions } from "@/lib/analytics";
import type { MouseEvent, ReactNode } from "react";

type Variant = "mint" | "outline" | "peach" | "ghost";

interface Props {
  children: ReactNode;
  id?: string;
  to?: string;
  href?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  variant?: Variant;
  arrow?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  /** Fires a `cta_click` analytics event on click. */
  trackId?: CtaId;
  /** Extra context for the analytics event (e.g. location: "header"). */
  trackProps?: TrackOptions;
}


const base =
  "inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 will-change-transform hover:-translate-y-[1px] active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const sizes = {
  sm: "px-4 py-2 text-sm rounded-full",
  md: "px-6 py-3.5 text-[15px] rounded-lg",
  lg: "px-7 py-4 text-base rounded-lg",
};

const variants: Record<Variant, string> = {
  mint: "bg-mint text-mint-foreground font-semibold hover:bg-[color-mix(in_oklab,var(--mint)_88%,white)] hover:shadow-[0_14px_40px_-12px] hover:shadow-mint/55",
  outline:
    "border border-white/25 bg-black/20 text-foreground backdrop-blur-sm hover:bg-white/8 hover:border-white/45",
  peach:
    "bg-peach text-peach-foreground font-semibold hover:bg-[color-mix(in_oklab,var(--peach)_92%,white)] hover:shadow-[0_8px_24px_-8px] hover:shadow-peach/60",
  ghost: "text-foreground hover:bg-white/5",
};

export function Button({
  children,
  id,
  to,
  href,
  onClick,
  variant = "mint",
  arrow = false,
  size = "md",
  className,
  type = "button",
  disabled = false,
  trackId,
  trackProps,
}: Props) {
  const cls = cn(base, sizes[size], variants[variant], className, disabled && "opacity-60 pointer-events-none");
  const label = typeof children === "string" ? children : trackProps?.label;
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (trackId) trackCta(trackId, { label, ...trackProps });
    onClick?.(event);
  };
  const inner = (
    <>
      <span>{children}</span>
      {arrow && <ArrowRight className="h-4 w-4" />}
    </>
  );
  if (to) return <Link id={id} to={to} className={cls} onClick={handleClick}>{inner}</Link>;
  if (href) return <a id={id} href={href} className={cls} onClick={handleClick}>{inner}</a>;
  return <button id={id} type={type} onClick={handleClick} disabled={disabled} className={cls}>{inner}</button>;
}
